import {
  Box,
  BoxProps,
  useBoolean,
  useFormControlContext,
  useToken,
} from '@chakra-ui/react';
import Editor from '@draft-js-plugins/editor';
import createImagePlugin from '@draft-js-plugins/image';
import { DraftHandleValue, EditorState, RichUtils } from 'draft-js';
import 'draft-js/dist/Draft.css';
import * as React from 'react';

import { useHandlePastedFiles } from '@/hooks/useHandlePastedFiles';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { createLinkPlugin } from '@/modules/draft-js-link-plugin';

import { MentionSuggestionsWrapper } from './components/MentionSuggestionsWrapper';
import { Toolbar } from './components/Toolbar/Toolbar';
import { useMentions } from './hooks/useMentions';

const linkPlugin = createLinkPlugin();
const imagePlugin = createImagePlugin();

export const TextEditor = React.forwardRef<
  Editor,
  {
    value?: EditorState;
    onChange?: (value: EditorState) => void;
    onBlur?: () => void;
    placeholder: string;
    ariaLabel: string;
    isMentionsActive?: boolean;
    hideToolbar?: boolean;
    onEditorFocus?: () => void;
    isDisabled?: boolean;
  } & Omit<BoxProps, 'value' | 'onChange' | 'placeholder' | 'ariaLabel'>
>(
  (
    {
      value: editorState,
      onChange: setEditorState,
      onBlur,
      placeholder,
      ariaLabel,
      isMentionsActive,
      hideToolbar,
      onEditorFocus,
      isDisabled: isDisabledProp,
      ...boxProps
    },
    ref
  ) => {
    const [blue500] = useToken('colors', ['blue.500']);
    const { getUrlsFromFiles, isUploading } = useHandlePastedFiles();
    const [isFocused, setIsFocused] = React.useState(false);
    const formControlContext = useFormControlContext();
    const [isPasting, setIsPasting] = useBoolean(false);

    const isLoading = isPasting || isUploading;
    const isDisabled =
      isDisabledProp || formControlContext?.isDisabled || isLoading;

    const {
      currentUserHasPermission,
      mentionSuggestionsProps,
      mentionPlugin,
      syncMentionSearch,
    } = useMentions();
    const canUseMentions = Boolean(
      isMentionsActive && currentUserHasPermission
    );

    if (!editorState || !setEditorState) {
      return null;
    }

    const shouldHidePlaceholder =
      editorState?.getCurrentContent().hasText() ||
      editorState?.getCurrentContent().getBlockMap().first().getType() !==
        'unstyled';

    const handleKeyCommand = (command: string): DraftHandleValue => {
      switch (command) {
        case 'bold':
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
          return 'handled';

        case 'italic':
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));

          return 'handled';

        case 'underline':
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));
          return 'handled';
      }

      return 'not-handled';
    };

    const handlePastedFiles = (files: File[]): DraftHandleValue => {
      setIsPasting.on();
      getUrlsFromFiles(files)
        .then((urls) =>
          urls.forEach((url) => {
            setEditorState(imagePlugin.addImage(editorState, url, {}));
          })
        )
        .finally(() => setIsPasting.off());
      return 'handled';
    };

    return (
      <Box
        sx={{
          '.public-DraftStyleDefault-block': {
            opacity: isDisabled ? 0.4 : undefined,
            fontWeight: 'normal',
          },
          '.public-DraftEditorPlaceholder-root, .public-DraftEditorPlaceholder-hasFocus':
            { color: 'gray.500' },
        }}
      >
        <Box
          borderRadius="md"
          border="1px"
          borderColor={isFocused ? 'blue.500' : 'inherit'}
          boxShadow={isFocused ? `0 0 0 1px ${blue500}` : undefined}
          p="4"
          {...boxProps}
        >
          <Editor
            ariaDescribedBy={undefined}
            ariaLabel={ariaLabel}
            ref={ref}
            handleKeyCommand={handleKeyCommand}
            onFocus={() => {
              setIsFocused(true);
              onEditorFocus && onEditorFocus();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur && onBlur();
            }}
            editorState={editorState}
            onChange={(nextState) => {
              setEditorState(nextState);
              if (canUseMentions) {
                syncMentionSearch(nextState);
              }
            }}
            spellCheck
            placeholder={shouldHidePlaceholder ? '' : placeholder}
            plugins={[
              ...(isMentionsActive
                ? [mentionPlugin, imagePlugin, linkPlugin]
                : [imagePlugin, linkPlugin]),
            ]}
            readOnly={isDisabled}
            handlePastedFiles={handlePastedFiles}
          />

          {isLoading && <Loading />}

          {!hideToolbar && (
            <Toolbar
              editorState={editorState}
              setEditorState={setEditorState}
              isDisabled={isDisabled}
            />
          )}

          {isMentionsActive && (
            <MentionSuggestionsWrapper
              MentionSuggestions={mentionPlugin.MentionSuggestions}
              {...mentionSuggestionsProps}
            />
          )}
        </Box>
      </Box>
    );
  }
);

TextEditor.displayName = 'TextEditor';
